/**
 * IDT (designer team) KPI ページのデータ取得・編集フック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI 取得フック / 戦闘力編集 mutation フック
 * @graph-connects /idt-kpi [calls] IDT KPI 一括取得 API
 * @graph-connects /idt-kpi/combat-power [calls] designer 戦闘力編集 API
 */

import {
  keepPreviousData,
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { idtKpiApi, IDT_STATUS_ID_TO_LABEL } from '../lib/api.js';
import type { IdtKpiResponse } from '../lib/api.js';

/**
 * idt-kpi クエリの共通オプション。データソース (BigQuery issues_raw) は Backlog から
 * 数分遅れで同期されるため、編集直後の自動 refetch で楽観更新が古い値に巻き戻るのを避ける
 * 目的で staleTime を長め・window focus refetch を無効化する。
 * @graph-connects none
 */
const IDT_KPI_QUERY_OPTIONS = {
  placeholderData: keepPreviousData,
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
} as const;

/** @graph-connects /idt-kpi [calls] IDT KPI 一括取得 API */
export function useIdtKpi(month?: string) {
  return useQuery({
    queryKey: ['idt-kpi', month ?? 'current'],
    queryFn: () => idtKpiApi.getData(month),
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * 複数月を一括取得する (月 view は 1 ヶ月、Q view は Q の 3 ヶ月)。履歴回溯の期間切替用。
 * @graph-connects /idt-kpi [calls] IDT KPI 一括取得 API (月ごと)
 */
export function useIdtKpiMonths(months: string[]) {
  return useQueries({
    queries: months.map((m) => ({
      queryKey: ['idt-kpi', m],
      queryFn: () => idtKpiApi.getData(m),
      ...IDT_KPI_QUERY_OPTIONS,
    })),
  });
}

/**
 * 担当案件一覧 (未完了・直近1ヶ月更新・期間非依存) を取得する。KPI の月次データとは別に
 * 一度だけ取得し、選択中の月/四半期に依らず、未完了かつ直近 1 ヶ月以内に更新のある案件を
 * 下段テーブルに表示する (MyPage 担当案件一覧看板と同じ期間非依存)。queryKey は
 * ['idt-kpi', 'active-cases'] で、案件編集の楽観更新 (useUpdateIdtCase) / 戦闘力編集後の
 * invalidate の前方一致対象に含まれる。
 * @graph-connects /idt-kpi/active-cases [calls] 担当案件一覧 (未完了・直近更新・期間非依存) 取得 API
 */
export function useIdtActiveCases() {
  return useQuery({
    queryKey: ['idt-kpi', 'active-cases'],
    queryFn: () => idtKpiApi.getActiveCases(),
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * 全体の月次デザイン評価系列を取得する (解像度 5 軸 + 貢献度 3 軸, 全期間)。
 * IDT 看板「デザイン評価」カードの radar (前月比較) / 推移折線の元データ。
 * @graph-connects /idt-kpi/design-evaluation [calls] デザイン評価 (全体) 取得 API
 */
export function useIdtDesignEvaluation() {
  return useQuery({
    queryKey: ['idt-kpi', 'design-evaluation'],
    queryFn: () => idtKpiApi.getDesignEvaluation(),
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * デザイナー個人別の月次デザイン評価系列を取得する (貢献度 3 軸, 全期間)。
 * karte モーダルが「対象デザイナーの前月評価」を全体と比較表示する元データ。
 * @graph-connects /idt-kpi/design-evaluation/by-designer [calls] デザイン評価 (デザイナー別) 取得 API
 */
export function useIdtDesignEvaluationByDesigner() {
  return useQuery({
    queryKey: ['idt-kpi', 'design-evaluation', 'by-designer'],
    queryFn: () => idtKpiApi.getDesignEvaluationByDesigner(),
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * 対象デザイナー×月の Good/More 定性コメントの AI 要約を取得する (karte の前月コメント要約用)。
 * nickname / month が揃ったときだけ発火する (karte を開いたときのみ AI 呼び出し)。
 * @graph-connects /idt-kpi/design-evaluation/comment-summary [calls] 評価コメント要約取得 API
 */
export function useIdtEvaluationCommentSummary(nickname: string | null, month: string) {
  return useQuery({
    queryKey: ['idt-kpi', 'comment-summary', nickname, month],
    queryFn: () => idtKpiApi.getEvaluationCommentSummary(nickname ?? '', month),
    enabled: nickname != null && month !== '',
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * 対象デザイナー×月の Good/More 定性コメントを明細で取得する (MyPage スキル評価のコメント詳細用)。
 * nickname / month が揃ったときだけ発火する。
 * @graph-connects /idt-kpi/design-evaluation/comment-detail [calls] 評価コメント明細取得 API
 */
export function useIdtEvaluationCommentDetail(nickname: string | null, month: string) {
  return useQuery({
    queryKey: ['idt-kpi', 'comment-detail', nickname, month],
    queryFn: () => idtKpiApi.getEvaluationCommentDetail(nickname ?? '', month),
    enabled: nickname != null && month !== '',
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/**
 * 対象月度の全メンバー評価明細を取得する (看板「デザイン評価(全体)」の明細モーダル用)。
 * enabled が false の間は発火しない (モーダルを開くまで sheet を読みに行かない)。
 * @graph-connects /idt-kpi/design-evaluation/comment-month-detail [calls] 対象月度の全メンバー評価明細取得 API
 */
export function useIdtEvaluationCommentMonthDetail(month: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['idt-kpi', 'comment-month-detail', month ?? 'latest'],
    queryFn: () => idtKpiApi.getEvaluationCommentMonthDetail(month ?? undefined),
    enabled,
    ...IDT_KPI_QUERY_OPTIONS,
  });
}

/** @graph-connects /idt-kpi/combat-power [calls] designer 戦闘力編集 API */
export function useUpdateIdtCombatPower() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; month: string; value: number }) =>
      idtKpiApi.updateCombatPower(args),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['idt-kpi'] });
    },
  });
}

/**
 * 担当案件明細の子チケット実績/ステータス編集 mutation。
 *
 * データソース (BigQuery) は Backlog から遅れて同期されるため、編集後に invalidate して
 * refetch すると「Backlog は更新済みだが BQ はまだ古い」値で表示が巻き戻る。これを避けるため
 * **楽観更新 (onMutate でキャッシュを即時更新)** し、成功時は refetch しない (BQ 同期後の
 * 再読み込みで正規値に収束する)。失敗時のみ元に戻す。
 *
 * @graph-connects /idt-kpi/cases/:childIssueKey [calls] 子チケット実績/ステータス編集 API
 */
export function useUpdateIdtCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { childIssueKey: string; actualHours?: number; statusId?: string }) =>
      idtKpiApi.updateCase(args.childIssueKey, {
        actualHours: args.actualHours,
        statusId: args.statusId,
      }),
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: ['idt-kpi'] });
      const previous = queryClient.getQueriesData<IdtKpiResponse>({ queryKey: ['idt-kpi'] });
      queryClient.setQueriesData<IdtKpiResponse>({ queryKey: ['idt-kpi'] }, (old) => {
        // ['idt-kpi'] は前方一致のため design-evaluation / comment-summary 等
        // cases を持たないクエリにも updater が適用される。それらに対し old.cases を
        // 触ると TypeError で onMutate が中断し mutationFn(PUT)が飛ばなくなるため、
        // cases 配列を持つクエリ (IdtKpiResponse) だけを楽観更新する。
        if (!old || !Array.isArray(old.cases)) return old;
        return {
          ...old,
          cases: old.cases.map((c) =>
            c.childIssueKey === args.childIssueKey
              ? {
                  ...c,
                  ...(args.actualHours !== undefined ? { actualPt: args.actualHours } : {}),
                  ...(args.statusId !== undefined
                    ? {
                        statusId: args.statusId,
                        status: IDT_STATUS_ID_TO_LABEL[args.statusId] ?? c.status,
                      }
                    : {}),
                }
              : c,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // 失敗時は編集前のキャッシュへロールバック
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
  });
}
